// Shared types, brand palette, and the stock-image catalog for the
// masthead-newsletter builder. Kept in a single non-React module so the sibling
// editor + HTML-generator files never circular-import each other.
//
// The template mirrors a trade-publication webinar/announcement invite (the
// "EE Times" reference the user shared): a slim announcement bar, a solid
// brand masthead, a title bar, a hero image with an optional "Presented by:"
// sponsor lockup, a date / register block, body copy, a centered CTA, and a
// dark footer with contact + social links. The reference's red is swapped for
// the restrained XOP brand navy (see MASTHEAD_ACCENT below).

// XOP brand palette — the ONE accent is navy (theme `primary`, `#2a3e6a`), with
// lime (`#acce3e`) used only as a hairline footer rule. These are hard-coded hex
// (not `theme.palette.*`) on purpose: this string HTML is emailed and rendered
// by mail clients that have no access to the MUI theme. They intentionally match
// `apps/web/src/styles/theme.ts` (`brand` / `brandFrom` / `brandLime`).
export const MASTHEAD_ACCENT = "#2a3e6a" // brand navy — masthead, buttons, links
export const MASTHEAD_ACCENT_DARK = "#1c2846" // deep navy — footer + depth
export const MASTHEAD_LIME = "#acce3e" // brand lime — accents (stripe, eyebrow, rule)
export const MASTHEAD_PAGE_BG = "#eaedf3" // soft cool-gray page background
export const MASTHEAD_TINT = "#f4f6fa" // faint cool tint (announcement band)
export const MASTHEAD_HAIRLINE = "#e6e9f0" // hairline dividers
export const MASTHEAD_TITLE = "#18213b" // near-navy heading ink
export const MASTHEAD_BODY = "#454f66" // body text
export const MASTHEAD_MUTED = "#8791a5" // muted / eyebrow / labels
export const MASTHEAD_FOOTER_BG = "#141d33" // deep navy footer (softer than near-black)

// The XOP brand mark ("the ball") — the same PNG the webinar + olive builders
// render in their headers. A relative `/stock-images/*` path so it resolves on
// the public support host in preview AND gets CID-inlined into the sent email
// (`EmailImageInliner`, xop-brain). Asset lives at
// xop-edge-customer/apps/portal-web/public/stock-images/xop-ball.png.
export const XOP_BRAND_MARK_URL = "/stock-images/xop-ball.png"

export interface MastheadTenantInfo {
  name: string
  logo_url?: string | null
  address?: string | null
  city?: string | null
  state?: string | null
  postal_code?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  website?: string | null
}

// One flat content bag — the masthead template is a single fixed layout (no
// add/remove/reorder), so unlike the webinar builder it needs just one section.
// Every field is optional-friendly: the generator supplies fallbacks and hides
// empty rows (announcement bar, sponsor lockup, "unable to attend" note, and the
// social row all disappear when their inputs are blank).
export interface MastheadContent {
  // Slim italic bar at the very top (e.g. a "now part of…" announcement). Hidden when blank.
  announcement: string
  // The masthead wordmark text (defaults to the tenant name). Rendered white on navy.
  masthead: string
  // The big serif title in the gray bar below the masthead.
  title: string
  // Hero image (a `/stock-images/*` stock photo or an `/api/v1/image/*` upload).
  imageUrl: string
  // Optional "Presented by:" sponsor logo overlaid bottom-right of the hero. Hidden when blank.
  sponsorLogoUrl: string
  // Event facts shown in the left column of the date/register row.
  date: string
  startTime: string
  sponsoredBy: string
  // The register button target (used by BOTH the top-right button and the bottom CTA).
  registrationLink: string
  // Italic "Unable to attend?" note above the body. Hidden when blank.
  unableToAttendNote: string
  // Rich-text body (TipTap HTML). Inline links inherit the navy accent.
  body: string
  // NB: footer social links are NOT per-campaign — every template's footer shows
  // the ONE fixed set from `crm/lib/email-social.ts` (EMAIL_SOCIAL_LINKS).
}

export interface MastheadSection {
  id: string
  type: "masthead"
  content: MastheadContent
}

// Hero image options — a curated slice of the shared webinar catalog plus the
// tech/infrastructure shots that suit an announcement/webinar invite.
export const mastheadImageOptions = [
  { id: 25, url: "/stock-images/server-room.jpg", name: "Server Room" },
  { id: 26, url: "/stock-images/control-room.jpg", name: "Control Room" },
  { id: 27, url: "/stock-images/network-infrastructure.jpg", name: "Network Infrastructure" },
  { id: 7, url: "/stock-images/tech-conference.jpg", name: "Tech Conference" },
  { id: 2, url: "/stock-images/virtual-meeting.jpg", name: "Virtual Meeting" },
  { id: 4, url: "/stock-images/digital-presentation.jpg", name: "Digital Presentation" },
  { id: 8, url: "/stock-images/modern-workspace.jpg", name: "Modern Workspace" },
  { id: 1, url: "/stock-images/conference-room.jpg", name: "Conference Room" },
]

// Re-exported from the single source of truth (`crm/lib/public-url.ts`) so this
// builder shares the one public host with the webinar + olive builders.
//   * toAbsoluteUrl     → SENT-EMAIL serializers (always the support host).
//   * toPreviewImageUrl → in-EDITOR <img> previews (uploads → dashboard proxy).
export { toAbsoluteUrl, toPreviewImageUrl } from "@crm-src/lib/public-url"
